import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useVisitor } from '../context/VisitorContext';
import { format, subDays, isAfter } from 'date-fns';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';

const { FiUsers, FiGlobe, FiTrendingUp, FiEye, FiLock, FiLogIn, FiAlertTriangle, FiDatabase } = FiIcons;

const Dashboard = () => {
  const { visitors, projects, isConnected, connectManual, login, user, tablesMissing } = useVisitor();
  
  // Login Form State
  const [authMode, setAuthMode] = useState('signin');
  const [manualCreds, setManualCreds] = useState({
    url: '',
    key: '',
    email: '',
    password: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Stats Calculation
  const today = new Date();
  const last7Days = subDays(today, 7);

  const todayVisitors = visitors.filter(v => 
    format(new Date(v.timestamp), 'yyyy-MM-dd') === format(today, 'yyyy-MM-dd')
  ).length;

  const weeklyVisitors = visitors.filter(v => 
    isAfter(new Date(v.timestamp), last7Days)
  ).length;

  // Chart Data
  const chartData = Array.from({ length: 7 }, (_, i) => {
    const date = subDays(today, 6 - i);
    const dayVisitors = visitors.filter(v => 
      format(new Date(v.timestamp), 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
    ).length;
    return {
      date: format(date, 'MMM dd'),
      visitors: dayVisitors
    };
  });

  const organizationCounts = visitors.reduce((acc, visitor) => {
    if (visitor.organization) {
      acc[visitor.organization] = (acc[visitor.organization] || 0) + 1;
    }
    return acc;
  }, {});

  const topOrganizations = Object.entries(organizationCounts)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 5)
    .map(([name, count]) => ({ name, count }));

  const stats = [
    { label: "Today's Visitors", value: todayVisitors, icon: FiEye, color: 'blue' },
    { label: 'This Week', value: weeklyVisitors, icon: FiUsers, color: 'green' },
    { label: 'Total Visitors', value: visitors.length, icon: FiTrendingUp, color: 'purple' },
    { label: 'Active Projects', value: projects.length, icon: FiGlobe, color: 'orange' }
  ];

  const handleConnectAndLogin = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // 1. Initialize Supabase Client
    if (!isConnected && manualCreds.url && manualCreds.key) {
      const connected = connectManual(manualCreds.url, manualCreds.key);
      if (!connected) {
        setIsSubmitting(false);
        return;
      }
    }

    // 2. Perform Auth
    await login(manualCreds.email, manualCreds.password, authMode === 'signup');
    setIsSubmitting(false);
  };

  const needsSetup = !isConnected || !user;

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="p-8">
      {/* Authentication Wall */}
      {needsSetup && (
        <div className="bg-white border border-blue-100 p-8 mb-8 rounded-xl shadow-lg max-w-3xl mx-auto">
          <div className="flex items-start mb-6">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
              <SafeIcon icon={FiLock} className="text-2xl text-blue-600" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900">Connect to Supabase</h3>
              <p className="text-gray-600 mt-2">
                Sign in to view your visitor analytics securely.
              </p>
            </div>
          </div>

          <form onSubmit={handleConnectAndLogin} className="space-y-6">
            {!isConnected && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="md:col-span-2">
                  <h4 className="font-semibold text-gray-700 mb-2">1. Project Configuration</h4>
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Project URL</label>
                  <input 
                    type="text" 
                    placeholder="https://your-project.supabase.co"
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                    value={manualCreds.url}
                    onChange={(e) => setManualCreds({...manualCreds, url: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Anon Key</label>
                  <input 
                    type="password" 
                    placeholder="eyJhbGciOiJIUzI1NiIsInR..."
                    className="w-full p-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                    value={manualCreds.key}
                    onChange={(e) => setManualCreds({...manualCreds, key: e.target.value})}
                    required
                  />
                </div>
              </div>
            )}

            <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
              <div className="flex justify-between items-center mb-4">
                <h4 className="font-semibold text-blue-900">2. Authentication</h4>
                <div className="flex bg-blue-100 rounded-lg p-1">
                  <button 
                    type="button"
                    onClick={() => setAuthMode('signin')}
                    className={`px-3 py-1 text-xs font-bold rounded ${authMode === 'signin' ? 'bg-white text-blue-700 shadow-sm' : 'text-blue-500 hover:text-blue-700'}`}
                  >
                    Sign In
                  </button>
                  <button 
                    type="button"
                    onClick={() => setAuthMode('signup')}
                    className={`px-3 py-1 text-xs font-bold rounded ${authMode === 'signup' ? 'bg-white text-blue-700 shadow-sm' : 'text-blue-500 hover:text-blue-700'}`}
                  >
                    Sign Up
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-blue-700 uppercase mb-1">Email</label>
                  <input 
                    type="email" 
                    placeholder="admin@example.com"
                    className="w-full p-2 border border-blue-200 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                    value={manualCreds.email}
                    onChange={(e) => setManualCreds({...manualCreds, email: e.target.value})}
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-blue-700 uppercase mb-1">Password</label>
                  <input 
                    type="password" 
                    placeholder="********"
                    className="w-full p-2 border border-blue-200 rounded focus:ring-2 focus:ring-blue-500 outline-none"
                    value={manualCreds.password}
                    onChange={(e) => setManualCreds({...manualCreds, password: e.target.value})}
                    required
                  />
                </div>
              </div>
            </div>

            <button 
              type="submit" 
              disabled={isSubmitting}
              className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition font-bold text-lg shadow-md flex justify-center items-center"
            >
              {isSubmitting ? (
                <SafeIcon icon={FiIcons.FiRefreshCw} className="animate-spin mr-2" />
              ) : (
                <SafeIcon icon={FiLogIn} className="mr-2" />
              )}
              {authMode === 'signin' ? 'Connect & Sign In' : 'Create Admin Account'}
            </button>
          </form>
        </div>
      )}

      {/* Database Critical Error */}
      {!needsSetup && tablesMissing && (
        <div className="bg-red-50 border border-red-200 p-6 mb-8 rounded-xl shadow-sm text-red-800">
          <div className="flex items-center mb-4">
            <SafeIcon icon={FiAlertTriangle} className="text-3xl text-red-600 mr-4" />
            <div>
              <h3 className="text-xl font-bold">Database Tables Missing</h3>
              <p className="mt-1">The app is connected to Supabase, but the required tables (<span className="font-mono bg-red-100 px-1 rounded">projects_v1</span>, <span className="font-mono bg-red-100 px-1 rounded">visitors_v1</span>) do not exist.</p>
            </div>
          </div>
          <div className="bg-white p-4 rounded border border-red-100">
            <p className="text-sm font-semibold mb-2 text-gray-700">Action Required:</p>
            <p className="text-sm text-gray-600 mb-2">
              Please copy the SQL below and run it in your <a href="https://supabase.com/dashboard/project/_/sql" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline hover:text-blue-800">Supabase SQL Editor</a> to fix the application.
            </p>
            <div className="relative">
              <pre className="bg-gray-900 text-green-400 p-4 rounded text-xs overflow-x-auto">
{`/* RUN THIS IN SUPABASE SQL EDITOR TO FIX THE APPLICATION */

CREATE TABLE IF NOT EXISTS projects_v1 (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  name text NOT NULL,
  url text NOT NULL,
  description text,
  created_at timestamptz DEFAULT now(),
  user_id uuid DEFAULT auth.uid()
);

CREATE TABLE IF NOT EXISTS visitors_v1 (
  id uuid PRIMARY KEY DEFAULT gen_random_uuid(),
  project_id uuid REFERENCES projects_v1(id) ON DELETE CASCADE,
  ip text,
  landing_page text,
  referrer text,
  user_agent text,
  screen_resolution text,
  visit_number int DEFAULT 1,
  duration int DEFAULT 0,
  utm_source text,
  utm_medium text,
  utm_campaign text,
  timestamp timestamptz DEFAULT now(),
  organization text
);

-- Security Policies
ALTER TABLE projects_v1 ENABLE ROW LEVEL SECURITY;
ALTER TABLE visitors_v1 ENABLE ROW LEVEL SECURITY;

CREATE POLICY "Users can manage own projects" 
  ON projects_v1 
  FOR ALL 
  TO authenticated 
  USING (auth.uid() = user_id OR user_id IS NULL)
  WITH CHECK (auth.uid() = user_id OR user_id IS NULL);

CREATE POLICY "Public can insert tracking data" 
  ON visitors_v1 
  FOR INSERT 
  TO anon 
  WITH CHECK (true);

CREATE POLICY "Users can view own visitor data" 
  ON visitors_v1 
  FOR SELECT 
  TO authenticated 
  USING (
    project_id IN (SELECT id FROM projects_v1 WHERE user_id = auth.uid() OR user_id IS NULL)
  );`}
              </pre>
            </div>
          </div>
        </div>
      )}

      {/* Main Dashboard Content */}
      {!needsSetup && !tablesMissing && (
        <>
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Dashboard</h1>
            <p className="text-gray-600">Welcome back, {user?.email}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-sm p-6 card-hover"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 mb-1">{stat.label}</p>
                    <p className="text-2xl font-bold text-gray-900">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 bg-${stat.color}-100 rounded-lg flex items-center justify-center`}>
                    <SafeIcon icon={stat.icon} className={`text-xl text-${stat.color}-600`} />
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Visitors (Last 7 Days)</h3>
              <ResponsiveContainer width="100%" height={300}>
                <LineChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Line 
                    type="monotone" 
                    dataKey="visitors" 
                    stroke="#3B82F6" 
                    strokeWidth={3}
                    dot={{ fill: '#3B82F6', strokeWidth: 2, r: 4 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl shadow-sm p-6"
            >
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Organizations</h3>
              {topOrganizations.length > 0 ? (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={topOrganizations}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" angle={-45} textAnchor="end" height={100} />
                    <YAxis />
                    <Tooltip />
                    <Bar dataKey="count" fill="#8B5CF6" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-64 text-gray-500 bg-gray-50 rounded-lg border border-dashed border-gray-200">
                  <div className="text-center">
                    <SafeIcon icon={FiUsers} className="text-4xl mb-2 mx-auto text-gray-300" />
                    <p>No visitor data yet</p>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default Dashboard;